// Session times are stored as ISO-8601 with an explicit offset
// (2026-09-06T19:00:00+03:00). Editors think in Cairo wall-clock time, so the
// form splits the value into a local date-time and an offset, and suggests
// Cairo's offset for the chosen date (+03:00 in summer, +02:00 in winter).

export const OFFSETS = ['+02:00', '+03:00', '+04:00', '+01:00', '+00:00'] as const

/** "2026-09-06T19:00:00+03:00" → { local: "2026-09-06T19:00", offset: "+03:00" } */
export function splitIso(iso: string | null | undefined): { local: string; offset: string } {
  const m = iso?.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::\d{2})?([+-]\d{2}:\d{2})$/)
  return m ? { local: m[1], offset: m[2] } : { local: '', offset: '' }
}

/** { local: "2026-09-06T19:00", offset: "+03:00" } → "2026-09-06T19:00:00+03:00" */
export function joinIso(local: string, offset: string): string {
  return local && offset ? `${local}:00${offset}` : ''
}

function offsetAt(instant: number, timeZone: string): string {
  const name = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
    .formatToParts(new Date(instant))
    .find((p) => p.type === 'timeZoneName')!.value // "GMT+03:00", or "GMT" for UTC
  return name === 'GMT' ? '+00:00' : name.slice(3)
}

/**
 * Cairo's UTC offset for a wall-clock time there. Tries each candidate offset
 * and keeps the one Cairo itself reports for that instant, which is right on
 * both sides of a daylight-saving change.
 */
export function cairoOffset(local: string, timeZone = 'Africa/Cairo'): string {
  for (const candidate of ['+03:00', '+02:00']) {
    const instant = Date.parse(`${local}:00${candidate}`)
    if (!Number.isNaN(instant) && offsetAt(instant, timeZone) === candidate) return candidate
  }
  return '+02:00'
}

/** "Sun 6 Sep 2026, 19:00 (Cairo)" for lists. */
export function formatCairo(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Cairo',
  }).format(new Date(iso))
}
